import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  BugReport as BugReportIcon,
  MergeType as MergeTypeIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { useAppSelector, type RootState } from "../../../store";
import { admin } from "../../../features/session/sessionSlice";
import firestoreInstance from "../../../services/firestoreInstance";
import {
  analyzePlayerDuplicates,
  type ExactDuplicateGroup,
  type PlayerDocRow,
  type PlayerDuplicatesReport,
} from "./analyzePlayerDuplicates";
import { mergeDuplicatePlayerLicenceGroup } from "./mergeDuplicatePlayersClient";

/** Même règle que la Cloud Function (tournoi courant + tie-break ID). */
function previewMergeTargetId(
  players: PlayerDocRow[],
  participationByPlayerId: Map<string, number>
): string {
  const ids = players.map((p) => p.docId).sort((a, b) => a.localeCompare(b));
  let targetId = ids[0];
  let maxC = participationByPlayerId.get(targetId) ?? 0;
  for (const id of ids) {
    const c = participationByPlayerId.get(id) ?? 0;
    if (c > maxC) {
      maxC = c;
      targetId = id;
    } else if (c === maxC && id.localeCompare(targetId) < 0) {
      targetId = id;
    }
  }
  return targetId;
}

function rowFromFirestoreDoc(
  doc: QueryDocumentSnapshot<DocumentData>
): PlayerDocRow {
  const d = doc.data();
  return {
    docId: doc.id,
    licenceId: d.licenceId != null ? String(d.licenceId) : "",
    name: d.name != null ? String(d.name) : "",
    surname: d.surname != null ? String(d.surname) : "",
    club: d.club != null ? String(d.club) : "",
    clubId: d.clubId != null ? String(d.clubId) : "",
    points: d.points != null ? String(d.points) : "",
    email: d.email != null ? String(d.email) : undefined,
  };
}

const PlayerDuplicatesAudit: React.FC = () => {
  const tournoi = useAppSelector((state: RootState) => state.tournoi.tournoi);
  const tournamentId = tournoi?.id;
  const isAdmin = useAppSelector(admin);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<PlayerDuplicatesReport | null>(null);
  const [participationByPlayerId, setParticipationByPlayerId] = useState<
    Map<string, number>
  >(() => new Map());
  const [totalPlayers, setTotalPlayers] = useState<number | null>(null);
  const [mergeDialogGroup, setMergeDialogGroup] =
    useState<ExactDuplicateGroup | null>(null);
  /** Licence en cours de fusion (un seul groupe à la fois, bouton unitaire par carte). */
  const [mergingLicenceId, setMergingLicenceId] = useState<string | null>(null);
  const [mergeFeedback, setMergeFeedback] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const db = firestoreInstance;
      const playersSnap = await getDocs(collection(db, "players"));
      const rows: PlayerDocRow[] = playersSnap.docs.map(rowFromFirestoreDoc);
      setTotalPlayers(rows.length);

      const partMap = new Map<string, number>();
      if (tournamentId) {
        const partSnap = await getDocs(
          collection(db, "tournois", tournamentId, "participation")
        );
        partSnap.forEach((doc) => {
          const pid = doc.data().playersId as string | undefined;
          if (pid) {
            partMap.set(pid, (partMap.get(pid) ?? 0) + 1);
          }
        });
      }
      setParticipationByPlayerId(partMap);

      setReport(analyzePlayerDuplicates(rows));
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : "Erreur lors du chargement Firestore.";
      setError(message);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  const participationLabel = (docId: string): string => {
    const n = participationByPlayerId.get(docId);
    if (n === undefined) {
      return tournamentId ? "0 (tournoi courant)" : "—";
    }
    return `${n} inscription(s) (tournoi courant)`;
  };

  const previewTargetForDialog = useMemo(() => {
    if (!mergeDialogGroup) {
      return null;
    }
    return previewMergeTargetId(
      mergeDialogGroup.players,
      participationByPlayerId
    );
  }, [mergeDialogGroup, participationByPlayerId]);

  const handleConfirmMerge = async () => {
    if (!mergeDialogGroup || !tournamentId || !isAdmin) {
      return;
    }
    const licence = mergeDialogGroup.licenceId;
    setMergingLicenceId(licence);
    setMergeFeedback(null);
    try {
      const data = await mergeDuplicatePlayerLicenceGroup(
        firestoreInstance,
        licence,
        tournamentId
      );
      setMergeFeedback({
        type: "success",
        text: `Fusion effectuée (${licence}). Fiche conservée : ${data.targetPlayerId}. Participations réattribuées : ${data.participationsRewritten}. Doublons tableau supprimés : ${data.participationsDroppedAsDuplicate}. Archives mises à jour : ${data.archivesUpdated}.`,
      });
      setMergeDialogGroup(null);
      await runAnalysis();
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Échec de la fusion (voir la console).";
      setMergeFeedback({ type: "error", text: msg });
    } finally {
      setMergingLicenceId(null);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: "auto" }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <BugReportIcon color="primary" />
        Audit des doublons joueurs (licenceId)
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Page non référencée dans le menu — accès direct par URL{" "}
        <code>/admin/player-duplicates-audit</code>. Lecture de la collection{" "}
        <code>players</code> et des inscriptions du tournoi courant pour le
        contexte. Chaque bouton « Fusionner ce numéro » traite{" "}
        <strong>un seul</strong> groupe (une licence à la fois), côté navigateur,
        avec les droits admin Firestore — pas de Cloud Function dédiée.
      </Alert>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={
            loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />
          }
          onClick={() => void runAnalysis()}
          disabled={loading}
        >
          Analyser
        </Button>
        {totalPlayers !== null && (
          <Typography variant="body2" color="text.secondary">
            {totalPlayers} fiche(s) joueur chargée(s)
            {tournamentId
              ? ` — tournoi courant : ${tournoi?.label ?? tournamentId}`
              : " — aucun tournoi courant (compteurs d’inscriptions indisponibles)"}
          </Typography>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {mergeFeedback && (
        <Alert
          severity={mergeFeedback.type}
          sx={{ mb: 2 }}
          onClose={() => setMergeFeedback(null)}
        >
          {mergeFeedback.text}
        </Alert>
      )}

      {report && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Chip
              color={report.exactDuplicates.length ? "error" : "default"}
              label={`Doublons exacts (même licenceId) : ${report.exactDuplicates.length} groupe(s)`}
            />
            <Chip
              color={report.normalizedCollisions.length ? "warning" : "default"}
              label={`Collisions normalisées : ${report.normalizedCollisions.length} groupe(s)`}
            />
            <Chip
              color={report.emptyOrMissingLicence.length ? "warning" : "default"}
              label={`Licence vide / manquante : ${report.emptyOrMissingLicence.length}`}
            />
          </Stack>
        </Stack>
      )}

      {report && report.exactDuplicates.length === 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Aucun doublon avec la même chaîne <code>licenceId</code>.
        </Alert>
      )}

      {report?.exactDuplicates.map((group) => (
        <Card key={group.licenceId} variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Même licenceId « {group.licenceId} » ({group.players.length}{" "}
              documents)
            </Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {group.hints.map((h) => (
                <Alert key={h} severity="info" variant="outlined">
                  {h}
                </Alert>
              ))}
            </Stack>
            <Stack direction="row" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
              <Tooltip
                title={
                  !isAdmin
                    ? "Réservé aux comptes avec le rôle admin (claim Firestore)."
                    : !tournamentId
                      ? "Sélectionnez un tournoi courant dans l’app pour déterminer quelle fiche conserver en priorité."
                      : `Uniquement la licence ${group.licenceId} : réécrit les inscriptions vers une fiche ; priorité au tournoi courant.`
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={
                      mergingLicenceId === group.licenceId ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <MergeTypeIcon />
                      )
                    }
                    disabled={
                      !isAdmin ||
                      !tournamentId ||
                      loading ||
                      mergingLicenceId !== null
                    }
                    onClick={() => setMergeDialogGroup(group)}
                  >
                    Fusionner ce numéro ({group.licenceId})
                  </Button>
                </span>
              </Tooltip>
            </Stack>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID document</TableCell>
                    <TableCell>Nom</TableCell>
                    <TableCell>Club</TableCell>
                    <TableCell>Points</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Inscriptions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.players.map((p) => (
                    <TableRow key={p.docId}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {p.docId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {p.surname} {p.name}
                      </TableCell>
                      <TableCell>{p.club}</TableCell>
                      <TableCell>{p.points}</TableCell>
                      <TableCell>{p.email ?? "—"}</TableCell>
                      <TableCell>{participationLabel(p.docId)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ))}

      {report && report.normalizedCollisions.length > 0 && (
        <>
          <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>
            Chaînes différentes pour une même clé normalisée
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            La normalisation retire espaces / séparateurs non numériques et les
            zéros de tête sur la partie numérique — deux saisies peuvent alors
            coexister dans Firestore sans être trouvées par une requête sur
            l’autre variante.
          </Typography>
          {report.normalizedCollisions.map((g) => (
            <Card key={g.normalizedKey} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Clé normalisée « {g.normalizedKey} » — variantes :{" "}
                  {g.distinctLicenceStrings.join(", ")}
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {g.hints.map((h) => (
                    <Alert key={h} severity="warning" variant="outlined">
                      {h}
                    </Alert>
                  ))}
                </Stack>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>licenceId (brut)</TableCell>
                        <TableCell>ID document</TableCell>
                        <TableCell>Nom</TableCell>
                        <TableCell>Club</TableCell>
                        <TableCell>Inscriptions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {g.players.map((p) => (
                        <TableRow key={`${p.docId}-${p.licenceId}`}>
                          <TableCell sx={{ fontFamily: "monospace" }}>
                            {JSON.stringify(p.licenceId)}
                          </TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>
                            {p.docId}
                          </TableCell>
                          <TableCell>
                            {p.surname} {p.name}
                          </TableCell>
                          <TableCell>{p.club}</TableCell>
                          <TableCell>{participationLabel(p.docId)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
        </>
      )}

      <Dialog
        open={mergeDialogGroup !== null}
        onClose={() => mergingLicenceId === null && setMergeDialogGroup(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Fusionner ce groupe uniquement</DialogTitle>
        <DialogContent>
          {mergeDialogGroup && (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Licence <strong>{mergeDialogGroup.licenceId}</strong> — traitement{" "}
                <strong>isolé</strong> pour ce numéro (les autres groupes ne sont
                pas modifiés). Les inscriptions sur <strong>tous les tournois</strong>{" "}
                pointant vers les fiches en trop seront réattribuées vers la fiche
                conservée. Si le même tableau était déjà inscrit sur la fiche
                cible, la participation en double est supprimée et le compteur du
                tableau est décrémenté.
              </Typography>
              {tournamentId && previewTargetForDialog && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Tournoi courant pour la priorité :{" "}
                  <strong>{tournoi?.label ?? tournamentId}</strong>
                  <br />
                  Fiche conservée (prévision) :{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ fontFamily: "monospace" }}
                  >
                    {previewTargetForDialog}
                  </Typography>{" "}
                  — celle qui a le plus d’inscriptions sur ce tournoi (ou ID le
                  plus petit à égalité).
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setMergeDialogGroup(null)}
            disabled={mergingLicenceId !== null}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={
              mergingLicenceId !== null || !tournamentId || !isAdmin
            }
            onClick={() => void handleConfirmMerge()}
          >
            {mergingLicenceId !== null ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Confirmer la fusion"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {report && report.emptyOrMissingLicence.length > 0 && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Fiches sans numéro de licence
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID document</TableCell>
                    <TableCell>Libellé</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.emptyOrMissingLicence.map((r) => (
                    <TableRow key={r.docId}>
                      <TableCell sx={{ fontFamily: "monospace" }}>{r.docId}</TableCell>
                      <TableCell>{r.label}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PlayerDuplicatesAudit;
